import type {
	ChatMentionedPayload,
	RealtimeMessage,
	TicketCommentMentionPayload,
	TicketMentionedPayload,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { requestOpenChatRail } from "../../lib/chat-rail"
import { useActiveChat } from "../active-chat-context"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useToast } from "../toast-context"
import { usePerUserChannelEvent } from "./use-per-user-channel-event"

/**
 * App-wide @-mention toasts (ZPL-147). Subscribes to the signed-in user's
 * per-user channel (`private-user-<id>`) so a mention surfaces from anywhere in
 * the app, and shows a toast unless the user is already looking at that chat
 * (open + window focused). Clicking the toast pre-opens the chat rail and
 * navigates to the property/project.
 */
export function useMentionToasts(myUserId: string | undefined): void {
	const navigate = useNavigate()
	const { activeChatId } = useActiveChat()
	const { show } = useToast()
	const { locale } = useLocale()
	const t = getTranslations(locale)

	// Keep the latest active chat in a ref so the bound handler stays stable
	// (no resubscribe churn when the open chat changes).
	const activeRef = useRef(activeChatId)
	activeRef.current = activeChatId

	const onMention = useCallback(
		(payload: ChatMentionedPayload) => {
			// Suppress only when the user is actively viewing that chat.
			if (payload.chatId === activeRef.current && document.hasFocus()) return
			show({
				title: t.chat.mention.toastTitle.replace(
					"{name}",
					payload.byDisplayName,
				),
				body: payload.preview,
				onClick: () => {
					requestOpenChatRail(payload.propertyId, payload.projectId)
					navigate(
						payload.projectId
							? webPaths.project(payload.propertyId, payload.projectId)
							: webPaths.property(payload.propertyId),
					)
				},
			})
		},
		[show, navigate, t],
	)

	const onTicketMention = useCallback(
		(payload: TicketMentionedPayload | TicketCommentMentionPayload) => {
			show({
				title: t.chat.mention.toastTitle.replace(
					"{name}",
					payload.byDisplayName,
				),
				body: payload.preview,
				onClick: () => {
					navigate(webPaths.ticket(payload.propertyId, payload.ticketId))
				},
			})
		},
		[show, navigate, t],
	)

	const handler = useCallback(
		(data: unknown) => {
			const msg = data as RealtimeMessage
			if (msg.event === "chat.mentioned") onMention(msg.payload)
		},
		[onMention],
	)
	const ticketHandler = useCallback(
		(data: unknown) => {
			const msg = data as RealtimeMessage
			if (
				msg.event === "ticket.mentioned" ||
				msg.event === "ticket.comment.mention"
			) {
				onTicketMention(msg.payload)
			}
		},
		[onTicketMention],
	)
	usePerUserChannelEvent(myUserId, "chat.mentioned", handler)
	usePerUserChannelEvent(myUserId, "ticket.mentioned", ticketHandler)
	usePerUserChannelEvent(myUserId, "ticket.comment.mention", ticketHandler)
}
